import '../css/base.css';
import '../css/main.css';

import * as THREE from './three.module.js';
import { OrbitControls } from './OrbitControls.js';

import * as dat from './lil-gui.module.min.js';

import { EffectComposer } from './EffectComposer.js';
import { RenderPass } from './RenderPass.js';
import { ShaderPass } from './ShaderPass.js';
import { UnrealBloomPass } from './UnrealBloomPass.js';
import { TextGeometry } from './TextGeometry.js';
import { FontLoader } from './FontLoader.js';

import { Util } from './models/Util.js';

//https://editor.p5js.org/marlontenorio/sketches/M_BGUpfKL
const TAU = 6.283185307179586;
const R = 500;
const xh = (angle) => (R / 15.0) * 16 * Math.pow(Math.sin(angle), 3);
const yh = (p) =>
  (R / 15.0) *
  (-13 * Math.cos(p) +
    5 * Math.cos(2 * p) +
    2 * Math.cos(3 * p) +
    Math.cos(4 * p));

// Triggers
const triggers = document
  .getElementsByClassName('triggers')[0]
  .querySelectorAll('span');

const cHeight = 1280,
  cWidth = 640;
const heartsNo = 30000;

let print = false;

const params = {
  print: {
    scale: 1.5,
    save: 1,
    positionZ: 1600,
    fogF: 0.0008,
    margin: 2000,
    sprite1Size: 10,
    sprite2Size: 20,
    exposure: 1.6,
    bloomStrength: 8, //1.5,
    bloomThreshold: 0,
    bloomRadius: 0.8, //1.5, //0,
    scene: 'Scene with Glow',
    randomness: 0.5,
    randomnessPower: 3,
    radius: 2,
  },
  dev: {
    scale: 0.5,
    save: false,
    positionZ: 1600,
    fogF: 0.0006,
    // fogF: 0.0004,
    margin: 0,
    sprite1Size: 10,
    sprite2Size: 15,
    exposure: 1.4,
    bloomStrength: 1.5, //1.5,
    // bloomStrength: 3, //1.5,
    bloomThreshold: 0,
    bloomRadius: 0.8, //1.5, //0,
    scene: 'Scene with Glow',
    randomness: 0.5,
    randomnessPower: 3,
    radius: 20,
  },
};

const globalUniforms = print ? params.print : params.dev;

const imageWidth = cHeight * globalUniforms.scale + globalUniforms.margin,
  imageHeight = cWidth * globalUniforms.scale + globalUniforms.margin;

const ENTIRE_SCENE = 0,
  BLOOM_SCENE = 1;

const bloomLayer = new THREE.Layers();
bloomLayer.set(BLOOM_SCENE);

const defaultAnimationSpeed = 1,
  morphAnimationSpeed = 3;
const normalSpeed = defaultAnimationSpeed / 100,
  fullSpeed = morphAnimationSpeed / 100;

let animationVars = {
  speed: normalSpeed,
};

const util = new Util();
const range =
  (1 - Math.log(util.getRandomInt(2, 256)) / Math.log(256)) * imageWidth * 4;

/**
 * Loaders
 */
// const cubeTextureLoader = new THREE.CubeTextureLoader();
const textureLoader = new THREE.TextureLoader();
const sprite6 = textureLoader.load('assets/textures/heart.png');

const color = new THREE.Color('#bb2646');
// const color = new THREE.Color('#b5314a');
const pointsMaterial = new THREE.PointsMaterial({
  color: color,
  size: globalUniforms.sprite1Size,
  map: sprite6,
  depthTest: false,
  transparent: true,
  blending: THREE.AdditiveBlending,
});

/**
 * Textures
 */

/**
 * Fonts
 */
const fontLoader = new FontLoader();

/**
 * Debug
 */
const gui = new dat.GUI({ closed: true, title: '', hidden: true });
const parameters = {
  displacementScale: 0,
};
gui.hide();

gui
  .add(globalUniforms, 'fogF', 0.0, 0.0009)
  .step(0.00001)
  .onChange(function (value) {
    scene.fog = new THREE.FogExp2(0x000000, value);
    render();
  });

gui.add(globalUniforms, 'exposure', 0.1, 2).onChange(function (value) {
  renderer.toneMappingExposure = Math.pow(value, 4.0);
  render();
});

gui.add(globalUniforms, 'bloomThreshold', 0.0, 1.0).onChange(function (value) {
  bloomPass.threshold = Number(value);
  render();
});

gui.add(globalUniforms, 'bloomStrength', 0.0, 10.0).onChange(function (value) {
  bloomPass.strength = Number(value);
  render();
});

gui
  .add(globalUniforms, 'bloomRadius', 0.0, 5.0)
  .step(0.01)
  .onChange(function (value) {
    bloomPass.radius = Number(value);
    render();
  });

// gui.close();

/**
 * Base
 */
// Canvas
const canvas = document.querySelector('canvas.webgl');

// Scene
const scene = new THREE.Scene();

/**
 * Lights
 */

var light = new THREE.AmbientLight(0xffffff, 3); // soft white light
scene.add(light);

var directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
scene.add(directionalLight);

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.addEventListener('resize', () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  1,
  10000
);

camera.position.y = 0;
camera.position.z = globalUniforms.positionZ;

scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.autoRotate = false;
controls.autoRotateSpeed *= 0.5;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  antialias: true,
  alpha: true,
  preserveDrawingBuffer: globalUniforms.save,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.toneMapping = THREE.ReinhardToneMapping;
// renderer.setClearColor('#fff', 0.5);

/**
 * Objects
 */

const renderPass = new RenderPass(scene, camera);
renderPass.clearColor = new THREE.Color(0, 0, 0);
renderPass.clearAlpha = 1;

const bloomPass = new UnrealBloomPass(
  new THREE.Vector2(sizes.width, sizes.height),
  1.5,
  0.4,
  0.85
);
bloomPass.exposure = Math.pow(globalUniforms.exposure, 4.0);
bloomPass.threshold = globalUniforms.bloomThreshold;
bloomPass.strength = globalUniforms.bloomStrength;
bloomPass.radius = globalUniforms.bloomRadius;

const bloomComposer = new EffectComposer(renderer);
bloomComposer.renderToScreen = false;
bloomComposer.addPass(renderPass);
bloomComposer.addPass(bloomPass);

const finalPass = new ShaderPass(
  new THREE.ShaderMaterial({
    uniforms: {
      baseTexture: { value: null },
      bloomTexture: { value: bloomComposer.renderTarget2.texture },
    },
    vertexShader: document.getElementById('vertexshader').textContent,
    fragmentShader: document.getElementById('fragmentshader').textContent,
    defines: {},
  }),
  'baseTexture'
);
finalPass.needsSwap = true;
finalPass.renderToScreen = true;
finalPass.material.transparent = true; // FIX

const finalComposer = new EffectComposer(renderer);
finalComposer.addPass(renderPass);
finalComposer.addPass(finalPass);

//

scene.fog = new THREE.FogExp2(0x000000, globalUniforms.fogF);

//

const matcapTexture = textureLoader.load('./assets/textures/3.png');
let text, text1, text2;

fontLoader.load('./fonts/love.json', (font) => {
  console.log('font loaded');

  const textGeometry = new TextGeometry(`Love`, {
    font,
    size: 60,
    height: 20,
    curveSegments: 40,
    bevelEnabled: true,
    bevelThickness: 40,
    bevelSize: 10,
    bevelOffset: 0,
    bevelSegments: 40,
  });
  textGeometry.center();

  const textMaterial = new THREE.MeshMatcapMaterial({
    matcap: matcapTexture,
    // color: 0xff947a,
    color: 0xbb2646,
  });
  text = new THREE.Mesh(textGeometry, textMaterial);
  // text.position.x = 100;
  // text.position.y = 0;

  // text.rotation.z = Math.PI * 0.05;
  // text.layers.enable(BLOOM_SCENE);
  scene.add(text);
  console.log('font added');

  if (globalUniforms.save) {
    setTimeout(function () {
      var img = canvas.toBlob(function (blob) {
        saveAs(blob, 'map.png');
      });
    });
  }
});

//

const createGlobe = (heartsNo, width, trigger = null) => {
  const vertices = [];

  let dummy = new THREE.Object3D();
  var distance = width;

  for (let i = 0; i < heartsNo / 4; i++) {
    dummy.position.randomDirection().setLength(width + 0.1);

    var theta = THREE.Math.randFloatSpread(width);
    var phi = THREE.Math.randFloatSpread(width);

    let x = distance * Math.sin(theta) * Math.cos(phi);
    let y = distance * Math.sin(theta) * Math.sin(phi);
    let z = distance * Math.cos(theta);

    // Randomness
    const randomX =
      Math.pow(Math.random(), globalUniforms.randomnessPower) *
      range *
      globalUniforms.randomness *
      globalUniforms.radius *
      50;
    const randomY =
      Math.pow(Math.random(), globalUniforms.randomnessPower) *
      range *
      globalUniforms.randomness *
      globalUniforms.radius *
      50;
    const randomZ =
      Math.pow(Math.random(), globalUniforms.randomnessPower) *
      range *
      globalUniforms.randomness *
      globalUniforms.radius *
      50;

    x += randomX;
    y += randomY;
    z += randomZ;

    if (i % 2 === 0) {
      x += randomX;
      y += randomY;
      z += randomZ;
      vertices.push(x, y, z);
    } else {
      x -= randomX;
      y -= randomY;
      z -= randomZ;
      vertices.push(x, y, z);
    }

    vertices.push(x, y, z);
  }

  if (trigger !== null) {
    triggers[trigger].setAttribute('data-disabled', false);
  }

  return vertices;
};

const createCircle = (heartsNo, width, height, trigger = null) => {
  const vertices = [];

  for (let i = 0; i < heartsNo; i++) {
    var rad = util.getRadian(util.getRandomInt(0, 360));
    var x = Math.cos(rad) * width;
    let y = Math.sin(rad) * height;
    var z = 0;

    // Randomness
    const randomX =
      Math.pow(Math.random(), globalUniforms.randomnessPower) *
      range *
      globalUniforms.randomness *
      globalUniforms.radius;
    const randomY =
      Math.pow(Math.random(), globalUniforms.randomnessPower) *
      range *
      globalUniforms.randomness *
      globalUniforms.radius;
    const randomZ =
      Math.pow(Math.random(), globalUniforms.randomnessPower) *
      range *
      globalUniforms.randomness *
      globalUniforms.radius;

    x += randomX;
    y += randomY;
    z += randomZ;

    if (i % 2 === 0) {
      x += randomX;
      y += randomY;
      z += randomZ;
      vertices.push(x, y, z);
    } else {
      x -= randomX;
      y -= randomY;
      z -= randomZ;
      vertices.push(x, y, z);
    }

    vertices.push(x, y, z);
  }

  if (trigger !== null) {
    triggers[trigger].setAttribute('data-disabled', false);
  }

  return vertices;
};

const createHeart = (heartsNo, width, height, trigger = null) => {
  const vertices = [];

  for (let i = 0; i < heartsNo; i++) {
    let x = width / 2 + xh((TAU * i) / heartsNo);
    let y = height / 2 + yh((TAU * i) / heartsNo);
    var z = 0;

    // Randomness
    const randomX =
      Math.pow(Math.random(), globalUniforms.randomnessPower) *
      range *
      globalUniforms.randomness *
      globalUniforms.radius;
    const randomY =
      Math.pow(Math.random(), globalUniforms.randomnessPower) *
      range *
      globalUniforms.randomness *
      globalUniforms.radius;
    const randomZ =
      Math.pow(Math.random(), globalUniforms.randomnessPower) *
      range *
      globalUniforms.randomness *
      globalUniforms.radius;

    x += randomX - imageWidth / 2;
    y += randomY - imageHeight / 2;
    z += randomZ;

    y = -y;

    if (i % 2 === 0) {
      x += randomX;
      y += randomY;
      z += randomZ;
      vertices.push(x, y, z);
    } else {
      x -= randomX;
      y -= randomY;
      z -= randomZ;
      vertices.push(x, y, z);
    }
  }

  if (trigger !== null) {
    triggers[trigger].setAttribute('data-disabled', false);
  }

  return vertices;
};

let initialPoints,
  globePoints,
  ellipsePoints,
  circlePoints,
  heartPoints,
  motoCafePoints,
  motoAdvPoints,
  motoSportPoints,
  motoChopperPoints,
  motoEnduroPoints;

createMoto(640, 1280, './assets/textures/chopper.png', motoChopperPoints, 4);
createMoto(1280, 2000, './assets/textures/enduro.png', motoEnduroPoints, 5);
createMoto(1024, 1280, './assets/textures/adv.png', motoAdvPoints, 6);
createMoto(1102, 1280, './assets/textures/cafe.png', motoCafePoints, 7);
createMoto(734, 1280, './assets/textures/sport.png', motoSportPoints, 8);

let initialVertices = [],
  globeVertices = [],
  ellipseVertices = [],
  circleVertices = [],
  heartVertices = [];

const initialPoints_geometry = new THREE.BufferGeometry();
const globePoints_geometry = new THREE.BufferGeometry();
const circlePoints_geometry = new THREE.BufferGeometry();
const ellipsePoints_geometry = new THREE.BufferGeometry();
const heartPoints_geometry = new THREE.BufferGeometry();
const motoPoints_geometry = new THREE.BufferGeometry();

// Initial
for (var p = 0; p < heartsNo; p++) {
  initialVertices.push(0, 0, 0);
}

initialPoints_geometry.setAttribute(
  'position',
  new THREE.Float32BufferAttribute(initialVertices, 3)
);

initialPoints = new THREE.Points(initialPoints_geometry, pointsMaterial);
initialPoints.layers.enable(BLOOM_SCENE);

// Globe
globeVertices = createGlobe(heartsNo, sizes.width * 0.4, null);

globePoints_geometry.setAttribute(
  'position',
  new THREE.Float32BufferAttribute(globeVertices, 3)
);

globePoints = new THREE.Points(globePoints_geometry, pointsMaterial);
globePoints.layers.enable(BLOOM_SCENE);

// Circle
circleVertices = createCircle(
  heartsNo,
  sizes.width * 0.4,
  sizes.width * 0.4,
  1
);

circlePoints_geometry.setAttribute(
  'position',
  new THREE.Float32BufferAttribute(circleVertices, 3)
);

circlePoints = new THREE.Points(circlePoints_geometry, pointsMaterial);
circlePoints.layers.enable(BLOOM_SCENE);

// Ellipse
ellipseVertices = createCircle(heartsNo, sizes.width * 0.4, sizes.width / 4, 2);

ellipsePoints_geometry.setAttribute(
  'position',
  new THREE.Float32BufferAttribute(ellipseVertices, 3)
);

ellipsePoints = new THREE.Points(ellipsePoints_geometry, pointsMaterial);
ellipsePoints.layers.enable(BLOOM_SCENE);

// Heart
heartVertices = createHeart(heartsNo, sizes.width / 3, sizes.width / 3, 3);

heartPoints_geometry.setAttribute(
  'position',
  new THREE.Float32BufferAttribute(heartVertices, 3)
);

heartPoints = new THREE.Points(heartPoints_geometry, pointsMaterial);
heartPoints.layers.enable(BLOOM_SCENE);

scene.add(initialPoints);
// scene.add(globePoints);
// scene.add(circlePoints);
// scene.add(ellipsePoints);
// scene.add(heartPoints);

animate();

// setTimeout(toChopper, 500);
setTimeout(toCircle, 500);

function toGlobe() {
  handleTriggers(0);
  morphTo(globePoints, 1);
}

function toCircle() {
  handleTriggers(1);
  morphTo(circlePoints, 1);
}

function toEllipse() {
  handleTriggers(2);
  morphTo(ellipsePoints, 1);
}

function toHeart() {
  handleTriggers(3);
  morphTo(heartPoints, 1);
}

function toChopper() {
  handleTriggers(4);
  createMoto(640, 1280, './assets/textures/chopper.png', motoChopperPoints, 4);
}
function toEnduro() {
  handleTriggers(5);
  createMoto(1280, 2000, './assets/textures/enduro.png', motoEnduroPoints, 5);
}

function toAdventure() {
  handleTriggers(6);
  createMoto(1024, 1280, './assets/textures/adv.png', motoAdvPoints, 6);
}
function toCafe() {
  handleTriggers(7);
  createMoto(1102, 1280, './assets/textures/cafe.png', motoCafePoints, 7);
}
function toSport() {
  handleTriggers(8);
  createMoto(734, 1280, './assets/textures/sport.png', motoSportPoints, 8);
}

function createMoto(cWidth, cHeight, imgSrc, motoPoints, trigger = null) {
  const vertices = [];
  let vertices2 = [];

  if (motoPoints) {
    morphTo(motoPoints);

    if (trigger !== null) {
      triggers[trigger].setAttribute('data-disabled', false);
    }
  } else {
    // scene.traverse(disposeMaterial);
    // scene.children.length = 0;

    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imgSrc;

    img.onload = function () {
      var canvas = document.createElement('canvas');
      canvas.width = cWidth;
      canvas.height = cHeight;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      var imageData = ctx.getImageData(0, 0, cWidth, cHeight);

      createPointCloud(imageData);
    };
  }

  function createPointCloud(imageData) {
    var pixelColor = new THREE.Color();

    let i = 0;

    for (var x = 0; x < cHeight; x++) {
      for (var y = 0; y < cWidth; y++) {
        i += 4;

        const r = imageData.data[i + 0] / 255;
        const g = imageData.data[i + 1] / 255;
        const b = imageData.data[i + 2] / 255;
        const a = imageData.data[i + 3] / 255;
        pixelColor.setRGB(r, g, b);

        if (pixelColor.r === 0) {
        } else {
          var pointX = x;
          let pointY = y;
          var pointZ = x;

          // Randomness
          const randomX =
            Math.pow(Math.random(), globalUniforms.randomnessPower) *
            range *
            globalUniforms.randomness *
            globalUniforms.radius;
          const randomY =
            Math.pow(Math.random(), globalUniforms.randomnessPower) *
            range *
            globalUniforms.randomness *
            globalUniforms.radius;
          const randomZ =
            Math.pow(Math.random(), globalUniforms.randomnessPower) *
            range *
            globalUniforms.randomness *
            globalUniforms.radius;

          if (i % 8 === 0) {
            vertices.push(pointX, pointY, pointZ);
          } else if (i % 12 === 0) {
            pointX += randomX;
            pointY += randomY;
            pointZ += randomZ;
            vertices.push(pointX, pointY, pointZ);
            vertices2.push(pointX, pointY, pointZ);
          } else {
            pointX -= randomX * 2;
            pointY -= randomY * 2;
            pointZ -= randomZ * 2;
            vertices2.push(pointX, pointY, pointZ);
          }
        }
      }
    }

    vertices2 = [...vertices, ...vertices2];
    motoPoints_geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(vertices2, 3)
    );
    console.log(vertices2.length);
    motoPoints = new THREE.Points(motoPoints_geometry, pointsMaterial);
    // scene.add(motoPoints);

    motoPoints.scale.set(
      globalUniforms.scale,
      globalUniforms.scale,
      globalUniforms.scale
    );

    motoPoints.layers.enable(BLOOM_SCENE);

    morphTo(motoPoints);

    if (trigger !== null) {
      triggers[trigger].setAttribute('data-disabled', false);
    }
  }

  render();
}

function disposeMaterial(obj) {
  if (obj.material) {
    obj.material.dispose();
  }
}

/**
 * Animate
 */

function animate() {
  requestAnimationFrame(animate);

  render();
}

function render() {
  renderer.toneMappingExposure = Math.pow(globalUniforms.exposure, 4.0);
  bloomPass.exposure = globalUniforms.exposure;
  bloomPass.threshold = globalUniforms.bloomThreshold;
  bloomPass.strength = globalUniforms.bloomStrength;
  bloomPass.radius = globalUniforms.bloomRadius;

  controls.update();

  renderer.clear();
  // updatePoints();
  // rotateCamera();

  // render scene with bloom
  renderBloom(true);

  // render the entire scene, then render bloom scene on top
  finalComposer.render();
}

function renderBloom() {
  camera.layers.set(BLOOM_SCENE);
  bloomComposer.render();
  camera.layers.set(ENTIRE_SCENE);
}

function morphTo(newPoints, resetPosition) {
  TweenMax.to(animationVars, 0.3, {
    ease: Power4.easeIn,
    speed: fullSpeed,
    onComplete: slowDown,
  });
  // console.log(newPoints.geometry.attributes.position.array);
  // initialPoints.material.color.setHex(color);

  gsap.to(initialPoints.geometry.attributes.position.array, {
    endArray: newPoints.geometry.attributes.position.array,
    duration: 2,
    // Make sure to tell it to update
    onUpdate: () =>
      (initialPoints.geometry.attributes.position.needsUpdate = true),
  });

  // for (var i = 0; i < initialPoints_geometry.vertices.length; i++) {
  //   TweenMax.to(initialPoints_geometry.vertices[i], 4, {
  //     ease: Elastic.easeOut.config(1, 0.75),
  //     x: newParticles.vertices[i].x,
  //     y: newParticles.vertices[i].y,
  //     z: newParticles.vertices[i].z,
  //   });
  // }

  if (resetPosition) {
    gsap.to(initialPoints.position, {
      x: 0,
      y: 0,
      duration: 2,
      // Make sure to tell it to update
      onUpdate: () =>
        (initialPoints.geometry.attributes.position.needsUpdate = true),
    });
    gsap.to(initialPoints.rotation, {
      y: 0,
      duration: 2,
      // Make sure to tell it to update
      onUpdate: () =>
        (initialPoints.geometry.attributes.position.needsUpdate = true),
    });
  } else {
    gsap.to(initialPoints.position, {
      x: 0,
      y: -sizes.height * 0.4,
      z: -200,
      // x: -sizes.width * 0.3,
      // y: -sizes.height * 0.4,
      duration: 2,
      // Make sure to tell it to update
      onUpdate: () =>
        (initialPoints.geometry.attributes.position.needsUpdate = true),
    });
    gsap.to(initialPoints.rotation, {
      y: Math.PI * 0.4,
      duration: 2,
      // Make sure to tell it to update
      onUpdate: () =>
        (initialPoints.geometry.attributes.position.needsUpdate = true),
    });
  }
}

function slowDown() {
  TweenMax.to(animationVars, 4, {
    ease: Power2.easeOut,
    speed: normalSpeed,
    delay: 1,
  });
}

triggers[0].addEventListener('click', toGlobe);
triggers[1].addEventListener('click', toCircle);
triggers[2].addEventListener('click', toEllipse);
triggers[3].addEventListener('click', toHeart);
triggers[4].addEventListener('click', toChopper);
triggers[5].addEventListener('click', toEnduro);
triggers[6].addEventListener('click', toAdventure);
triggers[7].addEventListener('click', toCafe);
triggers[8].addEventListener('click', toSport);

function handleTriggers(disable) {
  for (var x = 0; x < triggers.length; x++) {
    if (disable === x) {
      triggers[x].setAttribute('data-disabled', true);
    } else {
      triggers[x].setAttribute('data-disabled', false);
    }
  }
}
